import { uniqueId } from 'lodash-es';
import { type InputItem } from '@instamenu/ai-api';

// OpenAI Responses API limitation: Only images and PDFs can be uploaded directly in message content.
// These formats work with vision models for native processing (OCR, image understanding, PDF text extraction).
// Other file types (.docx, .txt, .csv, .xlsx, code files) must use the File API + Vector Stores,
// Attempting to upload unsupported formats results in: "Expected file type to be a supported format"
// https://community.openai.com/t/files-api-what-formats-are-supported/1273160
export const acceptedFileTypes = 'image/*,.pdf';

export const inferMimeType = (data: string, fallback = 'application/octet-stream') => {
  const match = data.match(/^data:([^;]+);base64,/);
  return match?.[1] ?? fallback;
};

export const inferAttachmentKind = (mimeType: string): 'image' | 'file' =>
  mimeType.startsWith('image/') ? 'image' : 'file';

export type AttachmentInput = {
  data: string;
  mimeType: string;
  name: string;
};

export type Attachment = {
  id: string;
  data: string;
  mimeType: string;
  name?: string;
  kind: 'image' | 'file';
};

export const createAttachment = (input: AttachmentInput): Attachment => {
  const mimeType = input.mimeType;
  return {
    id: uniqueId(),
    data: input.data,
    mimeType,
    name: input.name,
    kind: inferAttachmentKind(mimeType),
  };
};

export const attachmentToInputItems = (attachment: Attachment): InputItem[] => {
  if (attachment.kind === 'image')
    return [
      {
        role: 'user',
        type: 'message',
        content: [
          {
            type: 'image',
            url: attachment.data,
            mimeType: attachment.mimeType,
            name: attachment.name,
            id: attachment.id,
          },
        ],
      },
      {
        role: 'developer',
        type: 'message',
        content: [
          { type: 'text', text: `<previous_image_id>${attachment.id}</previous_image_id>` },
        ],
      },
    ];

  return [
    {
      role: 'user',
      type: 'message',
      content: [
        {
          type: 'file',
          data: attachment.data,
          mimeType: attachment.mimeType,
          name: attachment.name ?? 'attachment',
          id: attachment.id,
        },
      ],
    },
  ];
};
